import {gql} from '@apollo/client';
import React, {useEffect} from 'react';

import {AntibodyNumberingLayout} from 'constants/MapsConstants';
import {
  AntibodyNumberingChainType,
  AntibodyNumberingScheme,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {
  AaSequenceVersionAntibodyNumberingQuery,
  AaSequenceVersionAntibodyNumberingQueryVariables,
} from 'molbio/maps/linear/__generated/AaSequenceVersionAntibodyNumberingQuery';
import {AntibodyNumberingTrack, ResidueNumbering} from 'molbio/maps/linear/AntibodyNumbering';
import Type from 'molbio/maps/Type';
import {capitalize} from 'util/NameUtil';

export const AA_SEQUENCE_VERSION_ANTIBODY_NUMBERING_QUERY = gql`
  query AaSequenceVersionAntibodyNumberingQuery(
    $aaSequenceVersionSpecifiers: [AaSequenceVersionSpecifierInput!]!
  ) @skipAudit @engComponent(name: ANTIBODIES) {
    aaSequenceVersions(aaSequenceVersionSpecifiers: $aaSequenceVersionSpecifiers) {
      ... on AaSequenceVersionNode {
        dbId
        aminoAcids
        antibodyNumberingInfo {
          numbering
          numberingScheme
          organism
          chainType
          sourceAllele
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const SCHEME_DISPLAY_NAMES: Record<AntibodyNumberingScheme, string> = {
  [AntibodyNumberingScheme.KABAT]: 'Kabat',
  [AntibodyNumberingScheme.IMGT]: 'IMGT',
  [AntibodyNumberingScheme.EU]: 'EU',
};

const CHAIN_TYPE_DISPLAY_NAMES: Record<AntibodyNumberingChainType, string> = {
  [AntibodyNumberingChainType.HEAVY]: 'Heavy',
  [AntibodyNumberingChainType.KAPPA]: 'Kappa',
  [AntibodyNumberingChainType.LAMBDA]: 'Lambda',
  [AntibodyNumberingChainType.ALPHA]: 'Alpha',
  [AntibodyNumberingChainType.BETA]: 'Beta',
  [AntibodyNumberingChainType.GAMMA]: 'Gamma',
  [AntibodyNumberingChainType.DELTA]: 'Delta',
};

/**
 * Returns the stored numbering of the queried version, or null if it has none or it doesn't fit the
 * residues being drawn (e.g. while residue edits are unsaved).
 */
export function getStoredResidueNumbering(
  data: AaSequenceVersionAntibodyNumberingQuery,
  residues: string
): ResidueNumbering | null {
  const [version] = data.aaSequenceVersions;
  if (version?.__typename !== 'AaSequenceVersionNode' || version.antibodyNumberingInfo == null) {
    return null;
  }
  const {numbering, numberingScheme, organism, chainType, sourceAllele} = version.antibodyNumberingInfo;
  if (version.aminoAcids.toUpperCase() !== residues.toUpperCase() || numbering.length !== residues.length) {
    return null;
  }
  const summary = [
    SCHEME_DISPLAY_NAMES[numberingScheme],
    `${capitalize(organism)} ${CHAIN_TYPE_DISPLAY_NAMES[chainType]}`,
    sourceAllele,
  ]
    .filter((part) => part != null)
    .join(' · ');
  return {start: 0, labels: numbering, summary};
}

/**
 * Returns the antibody numbering stored on an AA sequence version, or null while it loads, if the
 * version has none, or if it doesn't fit the residues being drawn.
 */
export function useStoredResidueNumbering({
  aaSequenceId,
  aaSequenceVersionId,
  residues,
  skip = false,
}: {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  residues: string;
  skip?: boolean;
}): ResidueNumbering | null {
  const result = useSkippableBenchlingQuery<
    AaSequenceVersionAntibodyNumberingQuery,
    AaSequenceVersionAntibodyNumberingQueryVariables
  >(AA_SEQUENCE_VERSION_ANTIBODY_NUMBERING_QUERY, {
    skip,
    variables: {aaSequenceVersionSpecifiers: [{aaSequenceId, versionDbId: aaSequenceVersionId}]},
  });
  return result.type === QueryResultType.SUCCESS ? getStoredResidueNumbering(result.data, residues) : null;
}

export interface Props {
  aaSequenceId: string;
  aaSequenceVersionId: number;
  layout: AntibodyNumberingLayout;
  redraw: (boxHeight: number) => void;
  residues: string;
  setSelection: (start: number, end: number) => void;
  sliceStart: number;
  // Exclusive
  sliceEnd: number;
  // A function that takes the index of a residue and returns its x-axis position on the slice
  xFn: (residueIndex: number) => number;
}

/**
 * Displays the antibody numbering stored on an AA sequence version, on its sequence map.
 */
export default function StoredAntibodyNumberingSlice({
  aaSequenceId,
  aaSequenceVersionId,
  layout,
  redraw,
  residues,
  setSelection,
  sliceStart,
  sliceEnd,
  xFn,
}: Props): JSX.Element | null {
  const numbering = useStoredResidueNumbering({aaSequenceId, aaSequenceVersionId, residues});

  const boxHeight = Type.heights[layout.type.fontSize] + layout.vMargin;
  const hasNumbering = numbering != null;
  useEffect(() => {
    redraw(hasNumbering ? boxHeight : 0);
  }, [boxHeight, hasNumbering, redraw]);

  if (numbering == null) {
    return null;
  }

  return (
    <AntibodyNumberingTrack
      numberings={[numbering]}
      onAlignment={false}
      {...{layout, residues, setSelection, sliceStart, sliceEnd, xFn}}
    />
  );
}
